import { useQuery } from '@tanstack/react-query';
import { QueryKeys } from '@/types/query';
import { getDsaReportsByProductId } from '@/services/item';
import { toRQQuery } from '@/utilities/service';
import { getDsaReportsByClientId } from '@/services/client';
import { useCallback, useRef, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { ServerErrorTwo } from '@/types/service';

type DebouncedFunction<T extends unknown[]> = (...args: T) => void;

export function useDsaReport(productId?: number, clientId?: number) {
  const { isFetching, isRefetching, data, refetch, remove } = useQuery({
    queryKey: [QueryKeys.DSA_REPORTS, productId, clientId],
    queryFn: () => {
      if (productId) return getDsaReportsByProductId(productId);
      if (clientId) return getDsaReportsByClientId(clientId);
    },
    staleTime: 5 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    enabled: !!productId || !!clientId,
  });

  return toRQQuery(data, isFetching, isRefetching, refetch, remove);
}

export function useDebounce<T extends unknown[]>(callback: DebouncedFunction<T>, delay: number): DebouncedFunction<T> {
  const timeoutRef = useRef<NodeJS.Timeout>();

  return useCallback(
    (...args: T) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay],
  );
}

export const useErrorHandling = () => {
  /*
    Work with error response model:
    errors: {
      detail: string;
    };
   */
  const getErrorMsgFromResponse = (error: unknown) => {
    if (!error) return;

    let errMsg = '';

    if (!axios.isAxiosError(error)) {
      return 'Server error';
    }

    // eslint-disable-next-line no-restricted-syntax
    const axiosError = error as AxiosError<ServerErrorTwo>;

    const { data, status } = axiosError.response || {};

    if (typeof data === 'string') {
      return `${data}`;
    }
    if (!data) {
      return `${axiosError.message || 'Unknown error'}`;
    }

    if (status && status >= 500) {
      errMsg = 'Internal server error';
    } else {
      const { message = 'N/A' } = data || {};
      errMsg = message;
    }

    return errMsg;
  };

  return { getErrorMsgFromResponse };
};

export const useClipboardCopy = () => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const copy = (value: string) => {
    navigator.clipboard.writeText(value);
    setSnackbarOpen(true);
  };

  const onSnackbarClose = (_: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  return { snackbarOpen, copy, onSnackbarClose };
};
